import React, { useEffect, useState } from 'react';
import AdminShell from '../../components/admin/AdminShell';
import { adminApi } from '../../lib/api';
import { formatCurrency, getTodayString, getDateDaysAgo } from '../../lib/utils';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Download, TrendingUp, Users, CreditCard, UserX, BarChart2 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { LoadingBlock } from '../../components/common/States';

const GOLD = '#D4A843';
const SURFACE = '#1A1A1A';

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#1A1A1A] border border-[#2A2A2A] rounded-lg px-3 py-2 text-xs">
      <p className="text-[#B9B9B9] mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }}>{p.name}: {p.name === 'Revenue' ? formatCurrency(p.value) : p.value}</p>
      ))}
    </div>
  );
};

export default function ReportsPage() {
  const [dateFrom, setDateFrom] = useState(getDateDaysAgo(30));
  const [dateTo, setDateTo] = useState(getTodayString());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const res = await adminApi.getReportTotals(dateFrom, dateTo);
      setData(res.data);
    } catch { toast.error('Failed to load report'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchReport(); }, []);

  const summary = data?.summary || {};
  const daily = data?.daily || [];
  const bySource = data?.by_source || {};

  return (
    <AdminShell title="Reports & Analytics">
      <div className="space-y-6">
        {/* Date Range Filter */}
        <div className="surface-card card-highlight rounded-xl p-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="space-y-1">
              <Label className="text-[#B9B9B9] text-xs">From</Label>
              <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="bg-[#111111] border-[#2A2A2A] text-sm" />
            </div>
            <div className="space-y-1">
              <Label className="text-[#B9B9B9] text-xs">To</Label>
              <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="bg-[#111111] border-[#2A2A2A] text-sm" />
            </div>
            <Button onClick={fetchReport} disabled={loading} className="btn-gold h-9 px-5 text-sm">
              {loading ? 'Loading...' : 'Generate Report'}
            </Button>
            <div className="flex gap-2 ml-auto">
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5 bg-transparent border-[#2A2A2A] text-[#B9B9B9] hover:border-[#D4A843]/40 text-xs"
                onClick={() => adminApi.exportCheckIns(dateFrom, dateTo)}
                data-testid="export-checkins-report-button"
              >
                <Download size={12} /> Export Check-ins
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5 bg-transparent border-[#2A2A2A] text-[#B9B9B9] hover:border-[#D4A843]/40 text-xs"
                onClick={() => adminApi.exportPayments(dateFrom, dateTo)}
                data-testid="export-payments-report-button"
              >
                <Download size={12} /> Export Payments
              </Button>
            </div>
          </div>
        </div>

        {loading ? (
          <LoadingBlock rows={4} />
        ) : data ? (
          <>
            {/* Summary KPIs */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { label: 'Total Check-ins', value: summary.total_checkins, icon: Users },
                { label: 'Total Revenue', value: formatCurrency(summary.total_revenue), icon: TrendingUp },
                { label: 'Paid', value: summary.paid_count, icon: CreditCard },
                { label: 'No Shows', value: summary.no_show_count, icon: UserX },
              ].map(kpi => (
                <div key={kpi.label} className="surface-card card-highlight rounded-xl p-4">
                  <div className="flex items-start justify-between mb-2">
                    <p className="text-xs text-[#B9B9B9] uppercase tracking-wider">{kpi.label}</p>
                    <div className="w-7 h-7 rounded-lg bg-[#D4A843]/10 flex items-center justify-center">
                      <kpi.icon size={13} className="text-[#D4A843]" />
                    </div>
                  </div>
                  <p className="font-['EB_Garamond'] text-2xl text-[#F2F2F2]">{kpi.value}</p>
                </div>
              ))}
            </div>

            {/* Daily Chart */}
            {daily.length > 0 && (
              <div className="surface-card card-highlight rounded-xl p-5">
                <h2 className="font-['EB_Garamond'] text-lg text-[#F2F2F2] mb-4">Daily Revenue & Check-ins</h2>
                <div className="h-[200px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={daily} barSize={20}>
                      <XAxis
                        dataKey="date"
                        tick={{ fill: '#6F6F6F', fontSize: 10 }}
                        tickFormatter={d => d?.slice(5)}
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis
                        yAxisId="left"
                        orientation="left"
                        tick={{ fill: '#6F6F6F', fontSize: 10 }}
                        axisLine={false}
                        tickLine={false}
                        tickFormatter={v => `₹${(v/1000).toFixed(0)}k`}
                      />
                      <YAxis
                        yAxisId="right"
                        orientation="right"
                        tick={{ fill: '#6F6F6F', fontSize: 10 }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar yAxisId="left" dataKey="revenue" name="Revenue" fill={GOLD} radius={[4,4,0,0]} />
                      <Bar yAxisId="right" dataKey="checkins" name="Check-ins" fill="#2A2A2A" radius={[4,4,0,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* Source Breakdown */}
            {Object.keys(bySource).length > 0 && (
              <div className="surface-card card-highlight rounded-xl p-5">
                <h2 className="font-['EB_Garamond'] text-lg text-[#F2F2F2] mb-4">Performance by Source</h2>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-[#2A2A2A]">
                        {['Source', 'Check-ins', 'Guests', 'Revenue'].map(h => (
                          <th key={h} className="px-3 py-2 text-left text-xs text-[#6F6F6F] uppercase tracking-wider">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#1E1E1E]">
                      {Object.entries(bySource).map(([src, stats]) => (
                        <tr key={src} className="table-row-hover">
                          <td className="px-3 py-2.5 text-[#F2F2F2] capitalize">{src || 'direct'}</td>
                          <td className="px-3 py-2.5 text-[#B9B9B9]">{stats.checkins}</td>
                          <td className="px-3 py-2.5 text-[#B9B9B9]">{stats.guests}</td>
                          <td className="px-3 py-2.5 text-[#D4A843]">{formatCurrency(stats.revenue)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        ) : null}
      </div>
    </AdminShell>
  );
}
